import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Table, Input, Select, Button, Space, Tag, Typography, Popconfirm } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import masterService from "../../services/masterService";
import { useNotify } from "../../context/NotificationContext";
import { useCommon } from '../../context/CommonContext';  
const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

const OnBoardingList = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { setPageTitle } = useCommon();
  useEffect(() => {
    setPageTitle("OnBoarding");
  }, []);
  // State management
  const [onboardingList, setOnboardingList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  // Filters state
  const [filters, setFilters] = useState({
    search: "",
    status: null,
  });

  // What is typed in the box, kept separate from filters.search so the input stays
  // responsive while the term it actually searches on settles. The fetch effect
  // watches filters.search, so debouncing into it collapses a typed word down to a
  // single request instead of one per letter.
  const [searchInput, setSearchInput] = useState("");
  const searchDebounceRef = React.useRef(null);
  const SEARCH_DEBOUNCE_MS = 300;
  // Monotonic request id — responses from superseded requests are dropped so a
  // slow earlier search can't overwrite the results of the latest one.
  const requestSeqRef = React.useRef(0);

  // Plan selection state
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  // Delete a single onboarding item
  const handleDelete = async (mobId) => {
    try {
      setDeletingId(mobId);
      const response = await masterService.deleteOnBoarding(mobId);
      if (response?.data?.status) {
        notify.success({
          message: response.data.message || "Deleted successfully",
        });
        getData();
      } else {
        notify.error({
          message: response?.data?.message || "Failed to delete",
        });
      }
    } catch (e) {
      notify.error({ message: "Failed to delete" });
    } finally {
      setDeletingId(null);
    }
  };

  // Table columns configuration
  const columns = [
    {
      title: "S.No",
      key: "sno",
      width: 80,
      render: (text, record, index) => index + 1,
    },
    {
      title: "Order",
      dataIndex: "mob_order",
      key: "mob_order",
      width: 100,
      sorter: (a, b) => a.mob_order - b.mob_order,
    },
    {
      title: "ID",
      dataIndex: "mob_id",
      key: "mob_id",
      width: 100,
      sorter: (a, b) => a.mob_id - b.mob_id,
    },
    {
      title: "Name",
      dataIndex: "mob_name",
      key: "mob_name",
      sorter: (a, b) => a.mob_name.localeCompare(b.mob_name),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (text) => (
        <Tag color={text === "Y" ? "green" : "red"}>
          {text === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
      filters: [
        { text: "Active", value: "Y" },
        { text: "Inactive", value: "N" },
      ],
      onFilter: (value, record) => record.is_active === value,
    },
    {
      title: "Actions",
      key: "action",
      render: (text, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() =>
              navigate(`/onboarding/edit/${record.mob_id}`)
            }
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this onboarding item?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDelete(record.mob_id)}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              loading={deletingId === record.mob_id}
              size="small"
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Fetch onboarding data
  const getData = async () => {
    const requestId = ++requestSeqRef.current;
    try {
      setLoading(true);
      console.log("Fetching onboarding data with filters:", filters);

      const response = await masterService.getAllOnBoarding(filters);
      if (requestId !== requestSeqRef.current) return; // superseded by a newer request
      console.log("API Response:", response);

      if (response.data && response.data.status) {
        setOnboardingList(response.data.data || []);
        // Set the first item's plan_id as default
        if (response.data.data && response.data.data.length > 0) {
          setSelectedPlanId(response.data.data[0].plan_id);
        }
      } else {
        console.log("API returned no data or status false:", response.data);
        setOnboardingList([]);
      }
    } catch (error) {
      if (requestId !== requestSeqRef.current) return;
      console.error("Error fetching onboarding data:", error);
      notify.error({ message: "Failed to fetch onboarding data" });
      setOnboardingList([]);
    } finally {
      // Only the newest request clears the spinner — an overtaken one finishing
      // early would otherwise show the list as loaded while it is still loading.
      if (requestId === requestSeqRef.current) setLoading(false);
    }
  };

  // Fetch subscription plans
  const getSubscriptionPlans = async () => {
    try {
      const response = await masterService.getSubPlanList({isAll: true,status: "Y"});
      console.log("Subscription Plans Response:", response);

      if (response.data && response.data.status) {
        setSubscriptionPlans(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching subscription plans:", error);
      notify.error({ message: "Failed to fetch subscription plans" });
    }
  };

  // Handle search — Enter key / search icon applies immediately, cancelling any
  // pending debounce so the term is not searched twice.
  const handleSearch = (value) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const term = String(value ?? "").trim();
    setFilters((prev) => (prev.search === term ? prev : { ...prev, search: term }));
  };

  // Debounced typing in the search box
  const handleSearchChange = (e) => {
    const value = e.target.value ?? "";
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const term = value.trim();
      setFilters((prev) => (prev.search === term ? prev : { ...prev, search: term }));
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
  };

  React.useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  // Handle status filter change
  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }));
  };

  // Handle plan selection change
  const handlePlanChange = (value) => {
    setSelectedPlanId(value);
  };

  // Save order and plan
  const saveOrderAndPlan = async () => {
    try {
      setSaving(true);
      console.log("Saving order and plan:", {
        onboarding_arr: onboardingList,
        plan_id: selectedPlanId,
      });

      const response = await masterService.saveOnBoardingOrderAndPlan({
        onboarding_arr: onboardingList,
        plan_id: selectedPlanId,
      });

      console.log("Save Response:", response);

      if (response.data && response.data.status) {
        notify.success({
          message:
            response.data.message || "Order and plan saved successfully!",
        });
      } else {
        notify.error({
          message: response.data.message || "Failed to save order and plan",
        });
      }
    } catch (error) {
      console.error("Error saving order and plan:", error);
      notify.error({ message: "Failed to save order and plan" });
    } finally {
      setSaving(false);
    }
  };

  // Handle add new onboarding
  const handleAddNew = () => {
    navigate("/onboarding/add/new");
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    getData();
  }, [filters.search, filters.status]);

  // Load subscription plans on mount
  useEffect(() => {
    getSubscriptionPlans();
  }, []);

  // Header content with user info

  return (
    <Card>
      {/* Table Operations */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
            style={{ marginRight: "15px" }}
          >
            Add
          </Button>
        </div>

        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <Select
            placeholder="Status"
            style={{ width: 150 }}
            value={filters.status}
            onChange={handleStatusChange}
            allowClear
          >
            <Option value={null}>All</Option>
            <Option value="Y">Active</Option>
            <Option value="N">Inactive</Option>
          </Select>

          <Search
            placeholder="Search..."
            style={{ width: 300 }}
            value={searchInput}
            onChange={handleSearchChange}
            onSearch={handleSearch}
            allowClear
          />
        </div>
      </div>

      {/* Plan Selection and Save */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Text strong>Select Plan: {selectedPlanId}</Text>
          <Select
            style={{ width: 300 }}
            value={selectedPlanId}
            onChange={handlePlanChange}
            placeholder="Please select Plans"
            showSearch
            optionFilterProp="children"
          >
            {subscriptionPlans.map((plan) => (
              <Option key={plan.id} value={plan.id}>
                {plan.title}
              </Option>
            ))}
          </Select>
        </div>

        <Button type="primary" onClick={saveOrderAndPlan} loading={saving}>
          Save Order And Plan
        </Button>
      </div>

      {/* Data Table */}
      <Table
        columns={columns}
        dataSource={onboardingList}
        rowKey="mob_id"
        loading={loading}
        scroll={{ x: 600 }}
        size="middle"
        pagination={false}
      />
    </Card>
  );
};

export default OnBoardingList;
